/* eslint-disable array-callback-return */
import { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useNotification from "../../../hooks/useNotification";
import useLoader from "../../../hooks/useLoader";
import { useFieldArray, useForm as useReackHookForm } from "react-hook-form";
import { transformObjectValues } from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import { transformValues, removeEmptyValues } from "../../../utils/objectUtils";
import { v4 as uuidv4 } from "uuid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { Box, Card, Tooltip, Typography } from "@mui/material";
import ShipmentItemCard from "./components/ShipmentItemCard";
import Button from "../../../components/Button";
import { organizationPrimaryColors } from "../../../layouts/PermanentSidebarMUI";
import { useOrgan } from "../../../hooks/useOrgan";
import MainLayout from "../../../layouts/MainLayout";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const CreateShipmentsPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    post: baseServicePost,
    get: baseServiceGet,
    setFormErrors,
  } = useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();
  const location = useLocation();
  const from = location.state?.from || "/aluguel/carregamentos";

  const { load: loadMovementIds } = useLocalStorage("selectedMovementIds");
  const movementIds = loadMovementIds()[0]?.map((item) => item.value);

  const methods = useReackHookForm({
    defaultValues: {
      shipment: {
        movement_ids: movementIds,
      },
      items: [],
    },
  });
  const { control, handleSubmit, reset, watch, setValue } = methods;

  const { remove } = useFieldArray({
    control,
    name: "items",
  });

  const [addresses, setAddresses] = useState({});
  const [locations, setLocations] = useState({});
  const fieldsData = watch();

  const previousItemsRef = useRef([]);
  const queryClient = useQueryClient();

  const itemsWatch = watch("items");

  const {
    load: loadValidatedEquipmentsFromLocalStorage,
    remove: removeValidatedEquipmentIdFromLocalStorage,
  } = useLocalStorage(`shipmentReceivedEquipments_default`);

  const {
    load: loadValidatedVolumesFromLocalStorage,
    remove: removeValidatedVolumeIdFromLocalStorage,
  } = useLocalStorage(`shipmentReceivedVolumes_default`);

  // Query para buscar os itens do movimento
  const {
    data: fetchedMovementItems,
    isError: isErrorMovementItems,
    error: movementItemsError,
  } = useQueryWithLoader({
    queryKey: ["movementItems", movementIds?.length > 0],
    queryFn: async () => {
      const filters = {
        deleted_at: false,
        status_id: [1, 2],
        movement_id: movementIds,
      };
      const filtersTransformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTransformed,
        page: 1,
        perPage: 999999999999,
      };
      const response = await baseServiceGet(entities.movementItems.get, params);
      return response.result.data;
    },
    enabled: movementIds?.length > 0,
    keepPreviousData: true,
    refetchOnMount: true,
    staleTime: Infinity,
  });

  const movementItems = useMemo(() => {
    if (movementIds?.length === 0) return [];
    return fetchedMovementItems;
  }, [fetchedMovementItems, movementIds?.length]);

  // useEffect para lidar com erros de fetchedMovementItems
  useEffect(() => {
    if (isErrorMovementItems && movementItemsError) {
      console.error("Erro ao buscar itens do movimento:", movementItemsError);
      showNotification("error", "Erro ao carregar itens do movimento.");
    }
  }, [isErrorMovementItems, movementItemsError, showNotification]);

  // Query para buscar todos os equipamentos do movimento
  const { data: movementItemEquipmentsResponseData } = useQueryWithLoader({
    queryKey: ["movementItemEquipments"],
    queryFn: async () => {
      const filters = {
        deleted_at: false,
        movement_id: movementIds,
      };
      const filtersTransformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTransformed,
        page: 1,
        perPage: 999999999999,
      };
      const response = await baseServiceGet(
        entities.movementItemsEquipments.get,
        params,
      );
      return response.result.data;
    },
    staleTime: Infinity,
    // keepPreviousData: true,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: movementIds?.length > 0,
  });

  // Query para buscar todos os volumes do movimento
  const { data: movementItemVolumesResponseData } = useQueryWithLoader({
    queryKey: ["movementItemVolumes"],
    queryFn: async () => {
      const filters = {
        movement_id: movementIds,
      };
      const filtersTransformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTransformed,
        page: 1,
        perPage: 999999999999,
      };
      const response = await baseServiceGet(
        entities.movementItemsVolumes.get,
        params,
      );
      return response.result;
    },
    staleTime: Infinity,
    // keepPreviousData: true,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: movementIds?.length > 0,
  });

  // useEffect para processar os dados validados buscados do local storage
  useEffect(() => {
    const loadData = async () => {
      const storedEquipmentsIds =
        await loadValidatedEquipmentsFromLocalStorage();
      const storedVolumes = await loadValidatedVolumesFromLocalStorage();

      if (storedEquipmentsIds?.length > 0 || storedVolumes?.length > 0) {
        const movementItemEquipments =
          movementItemEquipmentsResponseData?.filter((movementItem) =>
            storedEquipmentsIds.includes(movementItem?.equipment?.id),
          );

        const movementItemVolumes =
          movementItemVolumesResponseData?.data.filter((volume) =>
            storedVolumes.includes(volume?.id),
          );

        const itemsToSet = [];

        movementItemEquipments?.forEach((movementItem) => {
          itemsToSet.push({
            identify: uuidv4().slice(0, 8),
            equipment_id: movementItem?.equipment?.id ?? "-",
            movement_item: movementItem ?? "-",
            recipient_customer:
              movementItem?.movement?.recipient_customer ?? "-",
            destination:
              movementItem?.movement?.order?.destination_order ?? "-",
            movement_item_id: movementItem.id ?? "-",
            equipment_name: movementItem?.equipment?.name ?? "-",
            equipment_number: movementItem?.equipment?.number ?? "-",
            serial_number: movementItem?.equipment?.serial_number ?? "-",
            address_id: movementItem?.order_item?.address_id ?? null,
            location_id: movementItem?.order_item?.location_id ?? null,
          });
        });

        movementItemVolumes?.forEach((item) => {
          itemsToSet.push({
            identify: uuidv4().slice(0, 8),
            volume_id: item?.id,
            volume: item,
            volume_name: `${item.code} - ${item.name}`,
            items: item.items,
          });
        });

        setValue("items", itemsToSet);
      }
    };

    loadData();
  }, [
    movementItemEquipmentsResponseData,
    movementItemVolumesResponseData,
    movementItems,
    loadValidatedEquipmentsFromLocalStorage,
    loadValidatedVolumesFromLocalStorage,
    setValue,
  ]);

  useEffect(() => {
    if (!Array.isArray(itemsWatch)) return;

    const newAddresses = {};
    const newLocations = {};

    itemsWatch.forEach((item, index) => {
      if (!item.equipment_id && !item.volume_id) return;

      const addressList =
        item?.movement_item?.order_item?.order?.recipient_customer?.addresses ||
        item?.movement?.order?.recipient_customer?.addresses ||
        [];

      // endereços
      newAddresses[index] = addressList?.map((a) => ({
        value: a.id,
        label: a.formatted_address,
      }));

      // endereço selecionado
      const selectedAddress = addressList?.find(
        (a) => Number(a.id) === Number(item.address_id),
      );

      // localizações
      newLocations[index] =
        selectedAddress?.locations?.map((loc) => ({
          value: loc.id,
          label: loc.formatted_location,
        })) || [];

      // valida location
      if (
        item.location_id &&
        !selectedAddress?.locations?.some(
          (loc) => Number(loc.id) === Number(item.location_id),
        )
      ) {
        setValue(`items[${index}].location_id`, "");
      }
    });

    setAddresses(newAddresses);
    setLocations(newLocations);
  }, [itemsWatch, setValue]);

  // useEffect para setar endereço e localização padrão
  useEffect(() => {
    fieldsData.items.forEach((item, index) => {
      if (!item.equipment_id || !item.volume_id) return;

      // 1. Endereço Padrão (OK)
      if (!item.address_id && addresses[index]?.length > 0) {
        setValue(`items[${index}].address_id`, addresses[index][0].value, {
          shouldDirty: true,
        });
      }

      // 2. Localização Padrão
      // Se item.location_id NÃO está definido E locations[index] EXISTE
      if (!item.location_id && locations[index]?.length > 0) {
        setValue(`items[${index}].location_id`, locations[index][0].value, {
          shouldDirty: true,
        });
      }

      // 💡 ADIÇÃO: Se as opções de localização sumiram, limpe o valor
      if (
        item.address_id &&
        locations[index]?.length === 0 &&
        item.location_id
      ) {
        setValue(`items[${index}].location_id`, "", { shouldDirty: true });
      }
    });
  }, [addresses, locations, fieldsData.items, setValue]);

  const handleDeleteSection = (indexToRemove) => {
    remove(indexToRemove);

    setAddresses((prevAddresses) => {
      const updated = {};
      Object.entries(prevAddresses).forEach(([key, value]) => {
        const oldIndex = parseInt(key, 10);
        if (oldIndex < indexToRemove) {
          updated[oldIndex] = value;
        } else if (oldIndex > indexToRemove) {
          updated[oldIndex - 1] = value;
        }
      });
      return updated;
    });

    setLocations((prevLocations) => {
      const updated = {};
      Object.entries(prevLocations).forEach(([key, value]) => {
        const oldIndex = parseInt(key, 10);
        if (oldIndex < indexToRemove) {
          updated[oldIndex] = value;
        } else if (oldIndex > indexToRemove) {
          updated[oldIndex - 1] = value;
        }
      });
      return updated;
    });

    const itemToRemove = fieldsData.items[indexToRemove];
    const equipmentIdToRemove = itemToRemove?.equipment_id;
    const volumeIdToRemove = itemToRemove?.volume_id;

    if (equipmentIdToRemove) {
      removeValidatedEquipmentIdFromLocalStorage(equipmentIdToRemove);
    }

    if (volumeIdToRemove) {
      removeValidatedVolumeIdFromLocalStorage(volumeIdToRemove);
    }
  };

  // Mutation para criar carregamento
  const createShipmentMutation = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePost(entities.shipments.create, data);
      return response;
    },
    onSuccess: (data) => {
      if (data?.status === 201) {
        reset();
        setAddresses({});
        setLocations({});
        previousItemsRef.current = [];

        // Invalida ShipmentsPage
        queryClient.invalidateQueries({ queryKey: ["shipments"] });
        queryClient.invalidateQueries({
          queryKey: ["movementShipments"],
        });
        queryClient.invalidateQueries({
          queryKey: ["movements"],
        });
        queryClient.invalidateQueries({
          queryKey: ["movementItems"],
        });
        queryClient.invalidateQueries({
          queryKey: ["expectedShipmentItems"],
        });
        queryClient.invalidateQueries({
          queryKey: ["expectedVolumes"],
        });
        queryClient.invalidateQueries({ queryKey: ["contractDetails"] });
        queryClient.invalidateQueries({ queryKey: ["contractItems"] });

        navigate(from, { replace: true });
      }
    },
    onError: (error) => {
      let notificationMessage = "Ocorreu um erro desconhecido.";
      setFormErrors({});

      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
        notificationMessage =
          error.response.data.message ||
          "Por favor, corrija os campos destacados.";
      } else if (error.response?.data?.message) {
        notificationMessage = error.response.data.message;
      } else if (error.message) {
        notificationMessage = error.message;
      }

      showNotification("error", notificationMessage);
      console.error("onError ", error);
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const onSubmit = async (data) => {
    try {
      if (!data.items || data.items?.length === 0) {
        showNotification(
          "warning",
          "Por favor, volte à página de validação e adicione pelo menos um item ao carregamento para continuar.",
        );
        return;
      }
      const itemsToSubmit = data.items?.map((item) => {
        const { identify, ...rest } = item;

        // se tiver equipment_id, remove os campos
        if (rest.equipment_id) {
          delete rest.movement_item;
        }

        // se tiver volume_id, remove os campos
        if (rest.volume_id) {
          delete rest.movement_item_id;
          delete rest.volume_name;
          delete rest.items;
          delete rest.address_id;
          delete rest.location_id;
        }

        return removeEmptyValues(rest);
      });

      const transformedData = {
        shipment: transformObjectValues(data.shipment),
        items: transformValues(itemsToSubmit),
      };

      await createShipmentMutation.mutateAsync(transformedData);
    } catch (error) {
      console.error("Erro ao enviar carregamento:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const isPageLoading = createShipmentMutation.isPending;

  useEffect(() => {
    if (isPageLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isPageLoading, showLoader, hideLoader]);

  const { selectedOrgan } = useOrgan();
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM;

  // * Agrupar por cliente e destino
  const grouped = fieldsData.items.reduce((acc, item) => {
    const customer =
      item.recipient_customer ??
      item.volume?.movement?.order?.recipient_customer;

    const customerId = customer?.id;
    const destination = item.destination ?? "-";

    const key = `${customerId}_${destination}`;

    if (!acc[key]) {
      acc[key] = {
        recipient_customer: customer,
        destination: destination,
        items: [],
      };
    }

    acc[key].items.push(item);

    return acc;
  }, {});

  const groupedArray = Object.values(grouped);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Cadastro de Carregamento`}
        showBackButton={true}
        backUrl={`/aluguel/carregamentos/validar`}
      />

      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ py: 2 }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              py: 1,
              px: 2,
              backgroundColor: primaryColor,
              color: "#ffffff",
              borderTopLeftRadius: "5px",
              borderTopRightRadius: "5px",
            }}>
            Equipamentos e Volumes
          </Typography>

          <Card
            variant="outlined"
            sx={{
              p: 1,
              mb: 2,
              borderTopLeftRadius: "0px",
              borderTopRightRadius: "0px",
              backgroundColor: "#f1f1f1",
            }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {groupedArray?.length > 0 &&
                groupedArray?.map((group, groupIndex) => (
                  <Box
                    key={group?.recipient_customer?.id || groupIndex}
                    sx={{ mb: 2, hover: { cursor: "pointer" } }}>
                    {/* Card do cliente */}
                    <Card
                      sx={{
                        p: 2,
                        backgroundColor: "#fcfcfc",
                      }}>
                      <Tooltip title={group.recipient_customer?.alias || ""}>
                        <Typography
                          fontWeight="bold"
                          variant="h6"
                          mb={1}
                          sx={{ borderBottom: "1px solid #ccc" }}>
                          {group.recipient_customer?.alias &&
                          group.destination?.name
                            ? `${group.recipient_customer.alias} - ${group.destination.name}`
                            : group.recipient_customer?.alias}
                        </Typography>
                      </Tooltip>

                      {/* Lista de equipamentos desse cliente */}
                      {group?.items?.map((item, itemIndex) => (
                        <Box
                          key={item.identify || itemIndex}
                          sx={{ display: "flex", gap: 1, mb: 1 }}>
                          <Card sx={{ flex: 1 }}>
                            <ShipmentItemCard
                              item={item}
                              itemNumber={itemIndex}
                              serial={item.serial_number || ""}
                              movementType={
                                Array.isArray(movementItems) &&
                                movementItems[itemIndex]
                                  ? movementItems[itemIndex].movement_name
                                  : ""
                              }
                              equipmentName={item.equipment_name || ""}
                              addressOptions={addresses[itemIndex] || []}
                              locationOptions={locations[itemIndex] || []}
                              onRemove={() => handleDeleteSection(itemIndex)}
                              control={control}
                              setValue={setValue}
                            />
                          </Card>
                        </Box>
                      ))}
                    </Card>
                  </Box>
                ))}
            </Box>
          </Card>
          {/* BOTÕES */}
          <Box display="flex">
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Button
                type="submit"
                text="Cadastrar"
                disabled={createShipmentMutation.isPending}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Button
                text="Voltar"
                link={`/aluguel/carregamentos/validar`}
                disabled={createShipmentMutation.isPending}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </MainLayout>
  );
};

export default CreateShipmentsPage;
